/**
 * Utility functions for discount management and caching
 */

import { DiscountDurationType, DiscountCache, Product } from "../types";

const DISCOUNT_CACHE_KEY = "discount_expiration_cache";
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Checks if a discount has expired based on creation date and duration
 * @param discountCreatedAt - The discount creation date
 * @param discountDuration - The duration of the discount
 * @param discountType - The type of duration (hours or days)
 * @returns True if the discount has expired, false otherwise
 */
export const isDiscountExpired = (
  discountCreatedAt: string | null | undefined,
  discountDuration: number | null | undefined,
  discountType: DiscountDurationType
): boolean => {
  if (!discountCreatedAt || !discountDuration) return true;

  const createdAt = new Date(discountCreatedAt);
  const now = new Date();
  const durationMs =
    discountType === "HOURS"
      ? discountDuration * 60 * 60 * 1000
      : discountDuration * 24 * 60 * 60 * 1000;
  const endTime = new Date(createdAt.getTime() + durationMs);

  return now > endTime;
};

/**
 * Retrieves cached expired discount IDs from localStorage
 * @returns Object containing expired discount IDs and cache timestamp
 */
const getCachedExpiredDiscounts = (): DiscountCache => {
  try {
    const cached = localStorage.getItem(DISCOUNT_CACHE_KEY);
    if (!cached) return { expiredIds: [], timestamp: 0 };

    const data: DiscountCache = JSON.parse(cached);
    const now = Date.now();

    if (now - data.timestamp > CACHE_DURATION) {
      localStorage.removeItem(DISCOUNT_CACHE_KEY);
      return { expiredIds: [], timestamp: 0 };
    }

    return data;
  } catch {
    return { expiredIds: [], timestamp: 0 };
  }
};

/**
 * Stores expired discount IDs in localStorage cache
 * @param expiredIds - Array of expired discount IDs
 */
const setCachedExpiredDiscounts = (expiredIds: string[]): void => {
  try {
    const data: DiscountCache = { expiredIds, timestamp: Date.now() };
    localStorage.setItem(DISCOUNT_CACHE_KEY, JSON.stringify(data));
  } catch {
    // Silently fail if localStorage is not available
  }
};

/**
 * Determines if a discount should be hidden based on expiration status
 * @param product - The product to check
 * @returns True if the discount should be hidden, false otherwise
 */
export const shouldHideDiscount = (product: Product): boolean => {
  if (!product?.rental?.hasDiscount) return true;

  const cached = getCachedExpiredDiscounts();
  if (product.id && cached.expiredIds.includes(product.id)) return true;

  const expired = isDiscountExpired(
    product.rental.discountCreatedAt,
    product.rental.discountDuration,
    product.rental.discountType || "DAYS"
  );

  if (expired && product.id && !cached.expiredIds.includes(product.id)) {
    setCachedExpiredDiscounts([...cached.expiredIds, product.id]);
  }

  return expired;
};

/**
 * Calculates the discount end time based on creation date and duration
 * @param discountCreatedAt - The discount creation date
 * @param discountDuration - The duration of the discount
 * @param discountType - The type of duration (hours or days)
 * @returns The end time as a Date object, or null if invalid data
 */
export const getDiscountEndTime = (
  discountCreatedAt: string | null | undefined,
  discountDuration: number | null | undefined,
  discountType: DiscountDurationType
): Date | null => {
  if (!discountCreatedAt || !discountDuration) return null;

  const createdAt = new Date(discountCreatedAt);
  const durationMs =
    discountType === "HOURS"
      ? discountDuration * 60 * 60 * 1000
      : discountDuration * 24 * 60 * 60 * 1000;

  return new Date(createdAt.getTime() + durationMs);
};

/**
 * Calculates the time remaining until discount expires
 * @param discountCreatedAt - The discount creation date
 * @param discountDuration - The duration of the discount
 * @param discountType - The type of duration (hours or days)
 * @returns Object with remaining time in different units, or null if expired/invalid
 */
export const getDiscountTimeRemaining = (
  discountCreatedAt: string | null | undefined,
  discountDuration: number | null | undefined,
  discountType: DiscountDurationType
): {
  hours: number;
  minutes: number;
  seconds: number;
  totalMs: number;
} | null => {
  const endTime = getDiscountEndTime(
    discountCreatedAt,
    discountDuration,
    discountType
  );
  if (!endTime) return null;

  const now = new Date();
  const remainingMs = endTime.getTime() - now.getTime();

  if (remainingMs <= 0) return null;

  const hours = Math.floor(remainingMs / (1000 * 60 * 60));
  const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((remainingMs % (1000 * 60)) / 1000);

  return { hours, minutes, seconds, totalMs: remainingMs };
};

/**
 * Formats discount time remaining as a readable string
 * @param timeRemaining - The time remaining object
 * @returns Formatted string showing time remaining
 */
export const formatDiscountTimeRemaining = (
  timeRemaining: { hours: number; minutes: number; seconds: number } | null
): string => {
  if (!timeRemaining) return "Истекла";

  const { hours, minutes, seconds } = timeRemaining;

  if (hours > 0) {
    return `${hours}ч ${minutes}м ${seconds}с`;
  } else if (minutes > 0) {
    return `${minutes}м ${seconds}с`;
  } else {
    return `${seconds}с`;
  }
};

/**
 * Clears the discount cache from localStorage
 */
export const clearDiscountCache = (): void => {
  localStorage.removeItem(DISCOUNT_CACHE_KEY);
};
